import { useState, useMemo } from "react";
import { num, toM, ml, m2, money, ceilStep, UNITS, CalcLayout, FormCol, Card, Field, Sel, HowTo, Advanced, AreaTable, sumArea, ResultCard, CutDiagram, useCatalog, ProductSelect } from "./shared";

const PATRON_DESP = { "Sin patrón": 5, "Repetición recta": 8, "Repetición alternada": 10, "Repetición libre": 15, "Mural": 20 };

export default function PapelTapiz({ onSave, s }) {
  const items = useCatalog("papel_tapiz");
  const [pid, setPid] = useState("");
  const prod = items.find((p) => p.id === pid);
  const [u, setU] = useState("m");
  const [tipo, setTipo] = useState("Vinílico");
  const [muros, setMuros] = useState([{ nombre: "Muro 1", largo: "4.50", ancho: "2.70", cant: "1" }]);
  const [descuentos, setDescuentos] = useState([]);
  const [rolloAncho, setRolloAncho] = useState("53");
  const [rolloLargo, setRolloLargo] = useState("10");
  const [patron, setPatron] = useState("Repetición recta");
  const [rapport, setRapport] = useState("64");
  const [margenSup, setMargenSup] = useState("5");
  const [margenInf, setMargenInf] = useState("5");
  const [despMode, setDespMode] = useState("auto");
  const [despManual, setDespManual] = useState("10");
  const [pegRend, setPegRend] = useState("25");
  const [precio, setPrecio] = useState("");
  const [precioInst, setPrecioInst] = useState("");
  const despPct = despMode === "auto" ? (PATRON_DESP[patron] ?? 10) : num(despManual);
  const pr = prod ? prod.price : num(precio);

  const r = useMemo(() => {
    const areaTotal = sumArea(muros, u), areaDesc = sumArea(descuentos, u);
    const areaNeta = Math.max(0, areaTotal - areaDesc);
    const anchoUtilM = num(rolloAncho) / 100;
    const rap = num(rapport) / 100;
    let totalTiras = 0, maxLargoTira = 0;
    muros.forEach((mu) => {
      const cant = Math.max(1, Math.round(num(mu.cant)) || 1);
      const anchoM = toM(mu.largo, u), altoM = toM(mu.ancho, u);
      if (anchoM <= 0 || altoM <= 0) return;
      const tiras = anchoUtilM > 0 ? Math.ceil(anchoM / anchoUtilM) * cant : 0;
      let largoTira = altoM + num(margenSup) / 100 + num(margenInf) / 100;
      if (patron !== "Sin patrón" && rap > 0) largoTira = ceilStep(largoTira, rap);
      totalTiras += tiras;
      maxLargoTira = Math.max(maxLargoTira, largoTira);
    });
    const tirasPorRollo = maxLargoTira > 0 ? Math.floor(num(rolloLargo) / maxLargoTira) : 0;
    const rollosFrac = tirasPorRollo > 0 ? totalTiras / tirasPorRollo : 0;
    const rollosDesp = rollosFrac * (1 + despPct / 100);
    const rollos = Math.ceil(rollosDesp - 1e-9);
    const cubetas = num(pegRend) > 0 ? Math.ceil(areaNeta / num(pegRend)) : 0;
    const aprov = rollos > 0 && tirasPorRollo > 0 ? (totalTiras / (rollos * tirasPorRollo)) * 100 : 0;
    const cMat = rollos * pr;
    const total = cMat + num(precioInst);
    const w = [];
    if (tirasPorRollo === 0 && totalTiras > 0) w.push("El alto del muro supera el largo del rollo: elige un rollo más largo o divide la tira.");
    if (patron !== "Sin patrón" && rap > 0) w.push("El rapport aumentó el largo de cada tira para no cortar el patrón.");
    if (prod && prod.stock < rollos) w.push(`Inventario insuficiente de "${prod.name}": stock ${prod.stock}, requieres ${rollos} rollos.`);
    const rows = [
      { section: "Proyecto" },
      { label: "Tipo de papel", value: tipo }, { label: "Número de muros", value: `${muros.length}` },
      { label: "Área total", value: m2(areaTotal) }, { label: "Área descontada", value: m2(areaDesc) },
      { label: "Área neta", value: m2(areaNeta) },
      { section: "Cálculo" },
      { label: "Ancho útil del rollo", value: ml(anchoUtilM) }, { label: "Rapport", value: patron === "Sin patrón" ? "—" : `${num(rapport)} cm` },
      { label: "Número total de tiras", value: `${totalTiras}` }, { label: "Largo de cada tira", value: ml(maxLargoTira) },
      { label: "Tiras por rollo", value: `${tirasPorRollo}` }, { label: "Desperdicio aplicado", value: `${despPct}%` },
      { label: "Aprovechamiento", value: `${aprov.toFixed(0)}%` },
      { label: "Rollos a vender", value: `${rollos} rollo(s)`, strong: true },
      { section: "Complementos" },
      { label: "Pegamento (cubetas)", value: `${cubetas}` },
      { label: "Kit de instalación", value: "Cutter, espátula, rodillo, cepillo, regla, nivel" },
      { section: "Costos" },
      { label: "Materiales", value: money(cMat) }, { label: "Instalación", value: money(num(precioInst)) },
      { label: "Total estimado", value: money(total), strong: true },
    ];
    const pieces = totalTiras > 0 && maxLargoTira > 0 ? [{ w: num(rolloAncho), h: maxLargoTira * 100, count: Math.min(totalTiras, tirasPorRollo || totalTiras) }] : [];
    return { rows, warnings: w, total, rollCm: num(rolloAncho), pieces, inputs: { u, tipo, rolloAncho } };
  }, [u, tipo, muros, descuentos, rolloAncho, rolloLargo, patron, rapport, margenSup, margenInf, despPct, pegRend, pr, precioInst, prod]);

  return (
    <CalcLayout>
      <FormCol>
        <HowTo title="¿Cómo calcular papel tapiz?"><p>Captura cada muro (ancho × alto), descuenta puertas/ventanas, define el rollo y el rapport. El sistema calcula tiras, tiras por rollo y rollos con desperdicio, sin cortar el patrón.</p></HowTo>
        <Card>
          <Sel label="Unidad" value={u} onChange={setU} opts={UNITS} testid="pt-unidad" />
          <Sel label="Tipo de papel" value={tipo} onChange={setTipo} opts={["Vinílico", "Tradicional", "Textil", "Autoadherible", "PVC", "Lavable", "Premium", "Mural", "Infantil", "Acústico", "Comercial"]} testid="pt-tipo" />
        </Card>
        <ProductSelect items={items} value={pid} onChange={setPid} testid="pt-product" />
        <AreaTable title="Muros (ancho × alto)" rows={muros} setRows={setMuros} u={u} defaultName="Muro" testid="pt-muros" />
        <AreaTable title="Descontar (puertas/ventanas)" rows={descuentos} setRows={setDescuentos} u={u} addLabel="+ Descuento" defaultName="Puerta" testid="pt-desc" />
        <Card>
          <Field label="Ancho del rollo (cm)" value={rolloAncho} onChange={setRolloAncho} step="1" suffix="cm" testid="pt-rolloancho" />
          <Field label="Largo del rollo (m)" value={rolloLargo} onChange={setRolloLargo} step="0.5" suffix="m" testid="pt-rollolargo" />
          <Sel label="Tipo de patrón" value={patron} onChange={setPatron} opts={["Sin patrón", "Repetición recta", "Repetición alternada", "Repetición libre", "Mural"]} testid="pt-patron" />
          {patron !== "Sin patrón" && <Field label="Rapport (cm)" value={rapport} onChange={setRapport} step="1" suffix="cm" testid="pt-rapport" />}
          <Sel label="Desperdicio" value={despMode} onChange={setDespMode} opts={[["auto", `Automático (${PATRON_DESP[patron] ?? 10}%)`], ["manual", "Manual"]]} testid="pt-despmode" />
          {despMode === "manual" && <Field label="Desperdicio (%)" value={despManual} onChange={setDespManual} step="1" suffix="%" testid="pt-despmanual" />}
        </Card>
        <Advanced>
          <Field label="Margen superior (cm)" value={margenSup} onChange={setMargenSup} step="1" suffix="cm" testid="pt-msup" />
          <Field label="Margen inferior (cm)" value={margenInf} onChange={setMargenInf} step="1" suffix="cm" testid="pt-minf" />
          <Field label="Rendimiento pegamento (m²/cubeta)" value={pegRend} onChange={setPegRend} step="1" suffix="m²" testid="pt-pegrend" />
          <Field label="Precio por rollo ($)" value={prod ? prod.price : precio} onChange={setPrecio} suffix="$" testid="pt-precio" />
          <Field label="Instalación ($)" value={precioInst} onChange={setPrecioInst} suffix="$" testid="pt-precio-inst" />
        </Advanced>
      </FormCol>
      <ResultCard title="Cotización de Papel Tapiz — GOÛT" modality="papel_tapiz" rows={r.rows} warnings={r.warnings} total={r.total} inputs={r.inputs} onSave={onSave}
        diagram={<CutDiagram rollCm={r.rollCm} pieces={r.pieces} />} />
    </CalcLayout>
  );
}
